import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import StandardField from "./StandardField";
import StateField from "./StateField";

const RenderFields = ({
  fields,
  formData,
  handleChange,
  readOnly = false,
  fieldTransformers = {},
  validationErrors = {},
}) => {
  const containerRef = useRef(null);
  const [numColumns, setNumColumns] = useState(1);
  const minColumnWidth = 410; // Minimum column width in pixels

  useEffect(() => {
    const updateColumns = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // Calculate how many columns fit based on the min column width
        const columns = Math.max(
          Math.floor(containerWidth / minColumnWidth),
          1
        );
        setNumColumns(columns);
      }
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // For each field, we create an array: first cell is the field, then (numColumns - 1) blank cells.
  const gridCells = fields.flatMap((field) => {
    const fieldReadOnly = field.forceReadOnly ? true : readOnly;
    let fieldComponent;
    if (field.key === "state" || field.key === "billingState") {
      fieldComponent = (
        <StateField
          key={field.key}
          field={field}
          formData={formData}
          handleChange={handleChange}
          readOnly={fieldReadOnly}
          validationErrors={validationErrors}
        />
      );
    } else {
      fieldComponent = (
        <StandardField
          key={field.key}
          field={field}
          formData={formData}
          handleChange={handleChange}
          readOnly={fieldReadOnly}
          validationErrors={validationErrors}
          fieldTransformer={fieldTransformers[field.key]}
        />
      );
    }
    // Create the row: first cell is the field; then add blank cells for the rest of the columns.
    const blankCells = Array.from({ length: numColumns - 1 }).map(
      (_, index) => <div key={`${field.key}-blank-${index}`} />
    );
    return [fieldComponent, ...blankCells];
  });

  return (
    <div
      ref={containerRef}
      className="grid gap-x-10 gap-y-2 w-full"
      style={{
        gridTemplateColumns: `repeat(${numColumns}, minmax(${minColumnWidth}px, 1fr))`,
      }}
    >
      {gridCells}
    </div>
  );
};

RenderFields.propTypes = {
  fields: PropTypes.array.isRequired,
  formData: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  fieldTransformers: PropTypes.object,
  validationErrors: PropTypes.object,
};

export default RenderFields;
